import {AnimatePresence, motion} from 'framer-motion';
import {
  Button,
  Card,
  Checkbox,
  CustomRequestOption,
  Label,
  openDialog,
  Table,
  TableRow,
  Tooltip,
  Upload,
  UploadState,
} from 'platform/components';
import {Box, Center, Hide, HStack, Link, Space, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {Dispatch, SetStateAction} from 'react';

import {always, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useUploadFileMutation} from '@dms/api/base';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {
  CSV_MIME_TYPE,
  DOC_MIME_TYPE,
  DOCX_MIME_TYPE,
  HEIC_MIME_TYPE,
  JPEG_MIME_TYPE,
  Nullish,
  openFile,
  PDF_MIME_TYPE,
  PNG_MIME_TYPE,
  useBoolean,
  XLS_MIME_TYPE,
  XLSX_MIME_TYPE,
} from 'shared/web';

import {convertEntityResourceIdToContextTarget} from '../../../utils/convertEntityResourceIdToContextTarget';
import {downloadFile} from '../../../utils/downloadFile';
import {getFileIcon} from '../../../utils/getFileIcon';
import {handleApiError} from '../../../utils/handleApiError';
import {EntityDocumentsSelectGrid} from '../../EntityDocumentsSelectGrid/EntityDocumentsSelectGrid';
import {UploadedCustomerMessageFile} from '../types/UploadedCustomerMessageFile';

interface MessageUploadProps {
  uploadedFiles: UploadedCustomerMessageFile[];
  setUploadedFiles: Dispatch<SetStateAction<UploadedCustomerMessageFile[]>>;
  resourceId?: EntityResourceIds | Nullish;
  recordId?: string | Nullish;
  vehicleId?: string | Nullish;
}

export function MessageUpload(props: MessageUploadProps) {
  const [isUploading, setIsUploading, stopIsUploading] = useBoolean();
  const [hasUploadError, showUploadError, hideUploadError] = useBoolean();

  const [uploadFile] = useUploadFileMutation();

  const contextTarget = props.resourceId
    ? convertEntityResourceIdToContextTarget(props.resourceId)
    : null;

  const uploadState = match<[boolean, boolean], UploadState | undefined>([
    hasUploadError,
    isUploading,
  ])
    .with([true, Pattern.boolean], always(UploadState.Error))
    .with([Pattern.boolean, true], always(UploadState.Uploading))
    .with([Pattern.boolean, false], always(UploadState.Success))
    .otherwise(always(undefined));

  const handleUpload = async ({file}: CustomRequestOption) => {
    try {
      const data = await uploadFile({file: file as File})
        .unwrap()
        .catch(handleApiError);

      if (!data) {
        return;
      }

      if (isNotNil(data)) {
        props.setUploadedFiles((prevState) => [
          ...prevState,
          {
            fileId: data.fileId,
            fileName: data.file.name,
            fileType: data.file.type,
            fileUrl: data.fileUri,
            shouldConvertToPdf: false,
          },
        ]);
      }
    } catch {
      showUploadError();
    } finally {
      stopIsUploading();
    }
  };

  const handleUploadFromSystem = () => {
    if (isNil(props.recordId) || isNil(contextTarget)) {
      throw new Error('Record ID and context target are required');
    }

    openDialog(
      <EntityDocumentsSelectGrid
        recordId={props.recordId}
        contextTarget={contextTarget}
        vehicleId={props.vehicleId}
        onSelect={(newFiles) => {
          props.setUploadedFiles((prevState) => [...prevState, ...newFiles]);
        }}
      />,
      {size: 'large', title: i18n.t('entity.document.labels.addDocumentsFromSystem')}
    );
  };

  return (
    <VStack>
      <VStack>
        <Label>{i18n.t('entity.document.labels.attachments')}</Label>

        <HStack align="center" spacing={2} wrap>
          <Tooltip label={i18n.t('entity.document.labels.attachmentsDescription')}>
            <Upload
              name="document"
              type="button"
              buttonVariant="secondary"
              size="default"
              accept={[
                XLSX_MIME_TYPE,
                XLS_MIME_TYPE,
                DOC_MIME_TYPE,
                DOCX_MIME_TYPE,
                PDF_MIME_TYPE,
                JPEG_MIME_TYPE,
                PNG_MIME_TYPE,
                HEIC_MIME_TYPE,
                CSV_MIME_TYPE,
              ].join(',')}
              isMultiple
              onStart={() => {
                hideUploadError();
                setIsUploading();
              }}
              uploadState={uploadState}
              uploadText={i18n.t('entity.document.actions.uploadDocument')}
              customRequest={handleUpload}
            />
          </Tooltip>

          <Hide when={isNil(props.recordId) || isNil(contextTarget)}>
            <Button
              variant="secondary"
              onClick={handleUploadFromSystem}
              leftIcon="content/add_circle"
              title={i18n.t('entity.document.labels.addDocumentsFromSystem')}
            />
          </Hide>
        </HStack>
      </VStack>

      <AnimatePresence>
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {isNotNilOrEmpty(props.uploadedFiles) && (
          <motion.div
            initial={{height: 0}}
            animate={{height: 'auto'}}
            exit={{height: 0}}
            transition={{duration: 0.2, ease: 'easeInOut'}}
            style={{overflow: 'hidden'}}
          >
            <Space vertical={2} />
            <Card
              variant="inlineWhite"
              isClosedByDefault
              isExpandable
              title={`${i18n.t('entity.documents.labels.uploadedFiles')} (${props.uploadedFiles.length})`}
              actions={[
                {
                  type: 'button',
                  title: i18n.t('entity.documents.labels.removeAllFiles'),
                  variant: 'dangerLink',
                  onClick: () => {
                    props.setUploadedFiles([]);
                  },
                },
              ]}
              data-testid="messageUpload-uploadedFilesCard"
            >
              <Table
                tableLayout="auto"
                variant="stripped"
                columns={[
                  {
                    element: (
                      <Box paddingLeft={2}>
                        <Label tooltip={i18n.t('entity.customer.labels.convertFilesToPdf')}>
                          {i18n.t('general.actions.convert')}
                        </Label>
                      </Box>
                    ),
                    width: '15%',
                  },
                  {
                    element: <Label>{i18n.t('entity.checkout.labels.file')}</Label>,
                  },
                  {
                    // needed for table to work correctly with row actions
                    element: <></>,
                  },
                ]}
              >
                {props.uploadedFiles.map((file) => (
                  <TableRow
                    key={file.fileId}
                    actions={{
                      primary: [
                        {
                          icon: 'file/download',
                          title: i18n.t('general.actions.download'),
                          onClick: () => downloadFile(file.fileUrl),
                        },
                        {
                          icon: 'action/delete',
                          title: i18n.t('general.actions.remove'),
                          severity: 'danger',
                          onClick: () =>
                            props.setUploadedFiles((prevState) =>
                              prevState.filter((item) => item.fileId !== file.fileId)
                            ),
                        },
                      ],
                    }}
                  >
                    <Center>
                      <Checkbox
                        value={file.shouldConvertToPdf}
                        isDisabled={file.fileType === PDF_MIME_TYPE}
                        onChange={(value) =>
                          props.setUploadedFiles((prevState) =>
                            prevState.map((item) => {
                              if (item.fileId === file.fileId) {
                                return {...item, shouldConvertToPdf: value};
                              }

                              return item;
                            })
                          )
                        }
                      />
                    </Center>

                    <VStack justify="center">
                      <Link
                        title={file.fileName}
                        leftIcon={getFileIcon(file.fileType)}
                        size="xSmall"
                        onClick={() => openFile(file.fileUrl)}
                      />
                    </VStack>
                  </TableRow>
                ))}
              </Table>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </VStack>
  );
}
